import { css, cx } from "@emotion/css";
import { useState, useTransition } from "react";
import { graphql, useFragment } from "react-relay";

import { Tab } from "../../../components/Tab";
import { VStack, HStack } from "../../../components/VStack";
import * as theme from "../../../theme";
import { VoteSortSelector } from "../../ProposalsListPage/VoteSortSelector";

import { FeedbackCastPanel } from "./Feedback/FeedbackCastPanel";
import { VotesCastPanel } from "./Votes//VotesCastPanel";
import { ProposalActionPanelFragment$key } from "./__generated__/ProposalActionPanelFragment.graphql";
import { VotesCastPanelFragment$key } from "./Votes/__generated__/VotesCastPanelFragment.graphql";
import { VotesOrder } from "./Votes/__generated__/VotesCastPanelPaginationQuery.graphql";
import { FeedbackCastPanelFragment$key } from "./Feedback/__generated__/FeedbackCastPanelFragment.graphql";

export function ProposalActionPanel({
  fragmentRef,
  votesCastFragmentRef,
  feedbackCastFragmentRef,
  expanded,
  votesSort,
  setVotesOrder,
}: {
  fragmentRef: ProposalActionPanelFragment$key;
  votesCastFragmentRef: VotesCastPanelFragment$key;
  feedbackCastFragmentRef: FeedbackCastPanelFragment$key;
  expanded: boolean;
  votesSort: VotesOrder;
  setVotesOrder: (order: VotesOrder) => void;
}) {
  const queryResult = useFragment(
    graphql`
      fragment ProposalActionPanelFragment on Query
      @argumentDefinitions(
        address: { type: "String!" }
        proposalId: { type: "ID!" }
        skipAddress: { type: "Boolean!" }
        orderBy: { type: "VotesOrder!" }
      ) {
        ...VotesCastPanelQueryFragment
          @arguments(
            address: $address
            proposalId: $proposalId
            skipAddress: $skipAddress
            orderBy: $orderBy
          )

        ...FeedbackCastPanelQueryFragment
          @arguments(
            proposalId: $proposalId
            skipAddress: $skipAddress
            address: $address
          )
      }
    `,
    fragmentRef
  );

  const [activeTab, setActiveTab] = useState<"VOTES" | "FEEDBACK">("VOTES");

  const [isVoteSortPending, startVoteSortTransition] = useTransition();

  return (
    <VStack
      justifyContent="space-between"
      gap="4"
      className={css`
        padding: ${theme.spacing["2"]} ${theme.spacing["0"]}
          ${theme.spacing["4"]} ${theme.spacing["0"]};
        height: 100%;
        font-size: ${theme.fontSize.xs};
        min-height: 0;
        @media (max-width: ${theme.maxWidth["2xl"]}) {
          gap: 0;
        }
      `}
    >
      <VStack
        gap="4"
        className={css`
          height: 100%;
          min-height: 0;
          flex-shrink: 1;
          padding-left: ${theme.spacing["4"]};
          padding-right: ${theme.spacing["4"]};
        `}
      >
        <HStack
          justifyContent="space-between"
          alignItems="center"
          className={css`
            flex-shrink: 0;
            padding-top: ${theme.spacing["2"]};
          `}
        >
          <HStack gap="3">
            <Tab
              name="VOTES"
              activePage={activeTab}
              className={css`
                font-size: ${theme.fontSize.base};
                font-weight: ${theme.fontWeight.bold};
                line-height: ${theme.lineHeight.normal};
              `}
              setActivePage={setActiveTab}
            />

            <Tab
              name="FEEDBACK"
              activePage={activeTab}
              className={css`
                font-size: ${theme.fontSize.base};
                font-weight: ${theme.fontWeight.bold};
                line-height: ${theme.lineHeight.normal};
              `}
              setActivePage={setActiveTab}
            />
          </HStack>

          <VoteSortSelector
            value={votesSort}
            onChange={(newSort) =>
              startVoteSortTransition(() => setVotesOrder(newSort))
            }
            size="m"
          />
        </HStack>
        {(() => {
          switch (activeTab) {
            case "VOTES":
              return (
                <VotesCastPanel
                  fragmentRef={votesCastFragmentRef}
                  queryFragmentRef={queryResult}
                  expanded={expanded}
                  isVoteSortPending={isVoteSortPending}
                />
              );

            case "FEEDBACK":
              return (
                <FeedbackCastPanel
                  fragmentRef={feedbackCastFragmentRef}
                  queryFragmentRef={queryResult}
                  expanded={expanded}
                />
              );
          }
        })()}
      </VStack>
    </VStack>
  );
}
